import { useState, useEffect, useCallback } from 'react'
import DatePicker from '../components/DatePicker'
import { useAuth } from '../hooks/useAuth'
import { fetchRows, createRow, updateRow } from '../lib/api'
import { supabase } from '../lib/supabase'
import { deleteWithUndo } from '../lib/undoDelete'
import { TABLES } from '../lib/tableConfig'

const T = TABLES.day_log

function getVal(v) {
  if (v === null || v === undefined) return ''
  if (typeof v === 'object' && 'value' in v) return v.value
  return v
}

export default function DayLog() {
  const { config } = useAuth()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editRow, setEditRow] = useState(null)
  const [form, setForm] = useState({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await fetchRows('day_log')
      setRows(data || [])
    } catch (e) {
      setError('Failed to load log entries')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  function openAdd() {
    const now = new Date()
    const date = now.toISOString().split('T')[0]
    const time = now.toTimeString().slice(0,5)
    setForm({ Date: date, Time: time })
    setEditRow(null)
    setShowModal(true)
  }

  function openEdit(row) {
    const f = {}
    T.formFields.forEach(field => {
      f[field.name] = getVal(row[field.name])
    })
    setForm(f)
    setEditRow(row)
    setShowModal(true)
  }

  async function handleSave() {
    setSaving(true)
    setError('')
    try {
      if (editRow) {
        await updateRow('day_log', editRow.id, form)
      } else {
        await createRow('day_log', form)
      }
      setShowModal(false)
      load()
    } catch (e) {
      setError('Failed to save entry')
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(row) {
    const label = 'log entry ' + (getVal(row['Date']) || '')
    if (!confirm('Delete the ' + label + '? You can undo it for 10 seconds.')) return
    try {
      await deleteWithUndo(supabase, { table: 'day_log', id: row.id, label, onUndone: () => load() })
      load()
    } catch (e) {
      setError('Failed to delete entry')
    }
  }

  const statusColor = (s) => {
    if (s === 'At Anchor') return { background: 'rgba(234,179,8,.15)', color: '#8a5c0a' }
    if (s === 'At Marina') return { background: 'rgba(52,181,120,.15)', color: '#137050' }
    return {}
  }

  return (
    <>

      <div style={{display:'flex',justifyContent:'flex-end',padding:'12px 16px',borderBottom:'1px solid var(--border)'}}>
        <button style={btnPrimary} onClick={openAdd}>+ New Entry</button>
      </div>

      {error && <div style={{color:'#b4313c',padding:'8px 16px'}}>{error}</div>}

      {loading ? (
        <div style={{padding:32,textAlign:'center',color:'var(--muted)'}}>Loading...</div>
      ) : rows.length === 0 ? (
        <div style={{padding:32,textAlign:'center',color:'var(--muted)'}}>
          No log entries yet. Click <strong>+ New</strong> to add the first entry.
        </div>
      ) : (
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{borderBottom:'1px solid var(--border)'}}>
                <th style={th}>Date</th>
                <th style={th}>Time</th>
                <th style={th}>Watch Keeper</th>
                <th style={th}>Status</th>
                <th style={th}>Position</th>
                <th style={th}>Wind</th>
                <th style={th}>Air °C</th>
                <th style={th}>Water °C</th>
                <th style={th}>Pressure</th>
                <th style={th}>Visibility</th>
                <th style={th}>Cloud</th>
                <th style={th}>Events</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id} style={{borderBottom:'1px solid var(--border)'}}
                  onMouseEnter={e => e.currentTarget.style.background='var(--surface2)'}
                  onMouseLeave={e => e.currentTarget.style.background=''}>
                  <td style={td}>{getVal(row['Date'])}</td>
                  <td style={td}>{getVal(row['Time'])}</td>
                  <td style={td}>{getVal(row['Watch Keeper'])}</td>
                  <td style={td}>
                    <span style={{...pill, ...statusColor(getVal(row['Status']))}}>{getVal(row['Status'])}</span>
                  </td>
                  <td style={td}>{getVal(row['Position'])}</td>
                  <td style={td}>{getVal(row['Wind Direction'])} {getVal(row['Wind Force']) !== '' ? `Bft ${getVal(row['Wind Force'])}` : ''}</td>
                  <td style={td}>{getVal(row['Air Temp'])}</td>
                  <td style={td}>{getVal(row['Water Temp'])}</td>
                  <td style={td}>{getVal(row['Pressure'])}</td>
                  <td style={td}>{getVal(row['Visibility'])}</td>
                  <td style={td}>{getVal(row['Cloud Cover'])}</td>
                  <td style={{...td, maxWidth:200, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{getVal(row['Significant Events'])}</td>
                  <td style={td}>
                    <div style={{display:'flex',gap:6}}>
                      <button style={btnSm} onClick={() => openEdit(row)}>Edit</button>
                      <button style={{...btnSm, color:'#b4313c'}} onClick={() => handleDelete(row)}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={overlay} onClick={() => setShowModal(false)}>
          <div style={modal} onClick={e => e.stopPropagation()}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
              <h3 style={{margin:0,fontSize:16,color:'#16233B'}}>{editRow ? 'Edit Log Entry' : 'New Log Entry'}</h3>
              <button style={closeBtn} onClick={() => setShowModal(false)}>✕</button>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              {T.formFields.map(field => {
                if (field.name === 'Significant Events') return null
                return (
                  <div key={field.name} style={{display:'flex',flexDirection:'column',gap:4}}>
                    <label style={{fontSize:11,fontWeight:600,letterSpacing:'.03em',color:'#5B6B82'}}>{field.label}{field.required ? ' *' : ''}</label>
                    {field.type === 'select' ? (
                      <select style={input} value={form[field.name] || ''} onChange={e => setForm({...form, [field.name]: e.target.value})}>
                        <option value=''>Select...</option>
                        {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : field.type === 'date' ? (
                      <DatePicker width="100%" value={form[field.name] || ''} onChange={v => setForm(f => ({ ...f, [field.name]: v }))} />
                    ) : (
                      <input style={input} type={field.type || 'text'} placeholder={field.placeholder || ''}
                        value={form[field.name] || ''}
                        onChange={e => setForm({...form, [field.name]: e.target.value})} />
                    )}
                  </div>
                )
              })}
            </div>

            {/* Significant Events full width */}
            <div style={{display:'flex',flexDirection:'column',gap:4,marginTop:12}}>
              <label style={{fontSize:11,fontWeight:600,letterSpacing:'.03em',color:'#5B6B82'}}>Significant Events</label>
              <textarea style={{...input, height:100, resize:'vertical'}}
                placeholder="Describe any significant events during this watch..."
                value={form['Significant Events'] || ''}
                onChange={e => setForm({...form, 'Significant Events': e.target.value})} />
            </div>

            {error && <div style={{color:'#b4313c',fontSize:12,marginTop:8}}>{error}</div>}

            <div style={{display:'flex',justifyContent:'flex-end',gap:8,marginTop:16}}>
              <button style={btnSecondary} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const th = { padding:'8px 12px', textAlign:'left', fontSize:11, color:'var(--muted)', fontWeight:600, whiteSpace:'nowrap' }
const td = { padding:'8px 12px', color:'var(--text)', verticalAlign:'middle' }
const pill = { display:'inline-block', padding:'2px 8px', borderRadius:20, fontSize:11, fontWeight:600 }
const btnSm = { padding:'3px 8px', fontSize:11, borderRadius:4, border:'1px solid var(--border)', background:'var(--surface2)', color:'var(--text)', cursor:'pointer' }
const overlay = { position:'fixed', inset:0, background:'rgba(15,34,64,.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }
const modal = { background:'#ffffff', color:'#16233B', boxShadow:'0 20px 60px rgba(15,34,64,.25)', borderRadius:12, padding:24, width:'min(700px,95vw)', maxHeight:'90vh', overflowY:'auto', border:'1px solid var(--border)' }
const input = { padding:'7px 10px', borderRadius:6, border:'1px solid var(--border)', background:'#ffffff', color:'#16233B', fontSize:13, width:'100%', boxSizing:'border-box', colorScheme:'light', minHeight:36 }
const closeBtn = { background:'none', border:'none', color:'var(--muted)', fontSize:18, cursor:'pointer' }
const btnPrimary = { padding:'8px 18px', borderRadius:6, border:'none', background:'var(--gold)', color:'#000', fontWeight:600, cursor:'pointer', fontSize:13 }
const btnSecondary = { padding:'8px 18px', borderRadius:6, border:'1px solid #CBD5E1', background:'#ffffff', color:'#16233B', cursor:'pointer', fontSize:13 }
